import {DATE_FORMATS, TIME_FORMATS} from "constants/AppConstants.js";
import EnsureUtil from "utils/EnsureUtil.js";
import ValidUtil from "utils/ValidUtil.js";

export interface DateDetails {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
  monthSortName: string;
  monthFullName: string;
  meridiem: string;
}

export default class DateTimeUtil {
  static monthSortNames: string[] = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];
  static monthFullNames: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  static monthFullNamesByLang: Record<string, string[]> = {
    en: DateTimeUtil.monthFullNames,
    fr: [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre"
    ],
    es: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre"
    ],
    it: [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre"
    ],
    de: [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember"
    ]
  };

  static getDateByTimeStamp(unixTimestamp: number): Date {
    const jsTimestamp = unixTimestamp * 1000;
    return new Date(jsTimestamp);
  }

  static getUTCDateByTimeStamp(unixTimestamp: number): Date {
    const currentDate = new Date();
    const offsetInSeconds = currentDate.getTimezoneOffset() * 60;
    const jsTimestamp = (unixTimestamp + offsetInSeconds) * 1000;
    return new Date(jsTimestamp);
  }

  static getUnixUTCTimestampByDate(date: Date): number {
    const offsetInSeconds = date.getTimezoneOffset() * 60;
    return Math.floor(date.getTime() / 1000) - offsetInSeconds;
  }

  static getUnixTimestampByDate(date: Date): number {
    return Math.floor(date.getTime() / 1000);
  }

  static removeTimeFromDate(date: Date): Date {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  static getDateDetails(date: Date, twentyFourHourFormat: boolean = false): DateDetails {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    let hours = twentyFourHourFormat ? date.getHours() : date.getHours() % 12;
    if (date.getHours() >= 12 && hours === 0) {
      hours = 12;
    }
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const milliseconds = date.getMilliseconds();
    const monthSortName = DateTimeUtil.monthSortNames[month];
    const monthFullName = DateTimeUtil.monthFullNames[month];
    const meridiem = date.getHours() >= 12 ? "PM" : "AM";

    return {year, month, day, hours, minutes, seconds, milliseconds, monthSortName, monthFullName, meridiem};
  }

  static convertSecondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  static convertSecondsToHours(seconds: number): number {
    const hours = seconds / 3600;
    return hours > 0 ? hours : 0;
  }

  static convertSecondsToTime(value: number, format: string): string {
    let output: string;
    let hours: number | string = 0;
    let minutes: number | string = 0;
    let seconds: number | string = 0;

    if (value > 0) {
      hours = Math.floor(value / 3600);
      minutes = Math.floor((value - hours * 3600) / 60);
      seconds = Math.floor((value - hours * 3600 - minutes * 60) * 100) / 100;
    }

    hours = EnsureUtil.ensureTwoDigitNumber(hours);
    minutes = EnsureUtil.ensureTwoDigitNumber(minutes);
    seconds = EnsureUtil.ensureTwoDigitNumber(seconds);

    switch (format) {
      case TIME_FORMATS.HHMM:
        output = `${hours}:${minutes}`;
        break;
      case TIME_FORMATS.HHMMSS:
        output = `${hours}:${minutes}:${seconds}`;
        break;
      default:
        throw new Error(`invalid time format: ${format}`);
    }

    return output;
  }

  static convertToPeriod(
    seconds: any,
    withHours: boolean = false,
    returnNull: boolean = false,
    long: boolean = true
  ): string | null {
    /**
     * Convert seconds to period depending on the value
     * It can return only seconds or minutes or hours or days or months or years
     * @param {number} seconds
     * @return {string} period
     */

    if (
      seconds === null ||
      seconds === undefined ||
      seconds.toString().length === 0 ||
      !isFinite(seconds) ||
      isNaN(seconds)
    ) {
      return returnNull ? null : "ND";
    }

    seconds = Number(seconds);

    let period = "";
    const minuteInSeconds = 60;
    const hourInSeconds = 3600;
    const dayInSeconds = 86400;
    const monthInSeconds = 2592000;
    const yearInSeconds = 31536000;

    let min = "minutes";
    let sec = "seconds";
    let hour = "hours";
    let day = "days";
    let month = "months";
    let year = "years";
    let and = "and";

    if (!long) {
      min = "min";
      sec = "sec";
      hour = "hr";
      day = "dd";
      month = "mm";
      year = "yy";
      and = ", ";
    }

    if (seconds < minuteInSeconds) {
      period = `${seconds.toFixed(0)} ${sec}`;
    } else if (seconds < hourInSeconds) {
      period = `${Math.floor(seconds / minuteInSeconds)} ${min} ${and} ${(seconds % minuteInSeconds).toFixed(0)} ${sec}`;
    } else if (seconds < dayInSeconds) {
      period = `${Math.floor(seconds / hourInSeconds)} ${hour}, ${Math.floor(
        (seconds % hourInSeconds) / minuteInSeconds
      )} ${min}`;
    } else if (seconds < monthInSeconds) {
      period = `${Math.floor(seconds / dayInSeconds)} ${day}, ${Math.floor(
        (seconds % dayInSeconds) / hourInSeconds
      )} ${hour}`;
    } else if (seconds < yearInSeconds) {
      period = `${Math.floor(seconds / monthInSeconds)} ${month}, ${Math.floor(
        (seconds % monthInSeconds) / dayInSeconds
      )} ${day}`;
      if (withHours) {
        period += `, ${Math.floor((seconds % dayInSeconds) / hourInSeconds)} ${hour}`;
      }
    } else {
      period = `${Math.floor(seconds / yearInSeconds)} ${year}, ${Math.floor(
        (seconds % yearInSeconds) / monthInSeconds
      )} ${month}`;
      if (withHours) {
        period += `, ${Math.floor(((seconds % yearInSeconds) % monthInSeconds) / dayInSeconds)} ${day}`;
        period += `, ${Math.floor((seconds % dayInSeconds) / hourInSeconds)} ${hour}`;
      }
    }

    return period;
  }

  static convertJsMonthToMonth(value: any): number {
    return parseInt(value) + 1;
  }

  static convertDateToString(date: Date, format: string, twentyFourHourFormat: boolean = false): string {
    let output: string;

    if (!ValidUtil.isDateValid(date)) {
      console.error(`invalid date: ${date}`);
      return "";
    }

    let {year, month, monthSortName, monthFullName, day, hours, minutes, seconds, meridiem}: Record<string, any> =
      DateTimeUtil.getDateDetails(date, twentyFourHourFormat);
    month = DateTimeUtil.convertJsMonthToMonth(month);

    month = EnsureUtil.ensureTwoDigitNumber(month);
    const dayWithoutTwoDigits = day;
    day = EnsureUtil.ensureTwoDigitNumber(day);
    hours = EnsureUtil.ensureTwoDigitNumber(hours);
    minutes = EnsureUtil.ensureTwoDigitNumber(minutes);
    seconds = EnsureUtil.ensureTwoDigitNumber(seconds);

    switch (format) {
      case DATE_FORMATS.HMM_Meridiem:
        output = `${hours}:${minutes} ${meridiem}`;
        break;
      case DATE_FORMATS.HMMSS_Meridiem:
        output = `${hours}:${minutes}:${seconds} ${meridiem}`;
        break;
      case DATE_FORMATS.DDMMYYYY:
        output = `${day}/${month}/${year}`;
        break;
      case DATE_FORMATS.YYYY_MM_DD:
        output = `${year}-${month}-${day}`;
        break;
      case DATE_FORMATS.DDMMMYYYY:
        output = `${day} ${monthSortName} ${year}`;
        break;
      case DATE_FORMATS.DDMMMMYYYY:
        output = `${day} ${monthFullName} ${year}`;
        break;
      case DATE_FORMATS.DDMMMYYYYATHMM_Meridiem:
        output = `${day} ${monthSortName} ${year} at ${hours}:${minutes} ${meridiem}`;
        break;
      case DATE_FORMATS.YYYYMMDDHHMMSSPlus0000:
        output = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000+0000`;
        break;
      case DATE_FORMATS.YYYYMMDDTHHMMSSZ:
        output = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
        break;
      case DATE_FORMATS.DD_MM_YYYY_HH_MM_SS:
        output = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        break;
      case DATE_FORMATS.DDSuffix_MM:
        // Display like "1st Nov", "30th Dec", ...
        // eslint-disable-next-line no-case-declarations
        const suffix =
          dayWithoutTwoDigits >= 11 && dayWithoutTwoDigits <= 13
            ? "th"
            : dayWithoutTwoDigits % 10 === 1
              ? "st"
              : dayWithoutTwoDigits % 10 === 2
                ? "nd"
                : dayWithoutTwoDigits % 10 === 3
                  ? "rd"
                  : "th";
        output = `${dayWithoutTwoDigits}${suffix} ${monthSortName}`;
        break;
      default:
        throw new Error(`invalid date format: ${format}`);
    }

    return output;
  }

  static convertStringToDate(value: string, format: string = DATE_FORMATS.YYYYMMDDHHMMSSPlus0000): Date {
    let output: Date;

    if (format === DATE_FORMATS.YYYYMMDDTHHMMSSZ || format === DATE_FORMATS.YYYYMMDDHHMMSSPlus0000) {
      const year: any = value.substring(0, 4);
      const month = value.substring(5, 7);
      const day: any = value.substring(8, 10);
      const hours: any = value.substring(11, 13);
      const minutes: any = value.substring(14, 16);
      const seconds: any = value.substring(17, 19);
      output = new Date(year, parseInt(month) - 1, day, hours, minutes, seconds);
    } else if (format === DATE_FORMATS.DDMMYYYY) {
      const day: any = value.substring(0, 2);
      const month = value.substring(3, 5);
      const year: any = value.substring(6, 10);
      output = new Date(year, parseInt(month) - 1, day);
    } else {
      output = new Date();
    }

    return output;
  }

  static getYesterdayDate(): Date {
    const today = new Date();
    return new Date(today.setDate(today.getDate() - 1));
  }

  static getThisWeekMondayDate(): Date {
    const dayOfTheWeekSundayValueInJs = 0;
    const today = new Date();
    const dayOfTheMonth = today.getDate();
    const dayOfTheWeek = today.getDay() === dayOfTheWeekSundayValueInJs ? 7 : today.getDay();
    return new Date(today.setDate(dayOfTheMonth - dayOfTheWeek + 1));
  }

  static getLastWeekMondayDate(): Date {
    const thisWeekMondayDate = DateTimeUtil.getThisWeekMondayDate();
    return new Date(thisWeekMondayDate.setDate(thisWeekMondayDate.getDate() - 7));
  }

  static getLastWeekSundayDate(): Date {
    const thisWeekMondayDate = DateTimeUtil.getThisWeekMondayDate();
    return new Date(thisWeekMondayDate.setDate(thisWeekMondayDate.getDate() - 1));
  }

  static getThisMonthStartDate(): Date {
    const today = new Date();
    return new Date(today.setDate(1));
  }

  static getLastMonthStartDate(): Date {
    const today = new Date();
    today.setDate(1);
    return new Date(today.setMonth(today.getMonth() - 1));
  }

  static getLastMonthEndDate(): Date {
    const today = new Date();
    return new Date(today.setDate(0));
  }

  static getLastThreeMonthStartDate(): Date {
    const today = new Date();
    today.setDate(1);
    return new Date(today.setMonth(today.getMonth() - 2));
  }

  static getThisYearStartDate(): Date {
    const today = new Date();
    today.setDate(1);
    return new Date(today.setMonth(0));
  }

  static getLastYearStartDate(): Date {
    const today = new Date();
    today.setDate(1);
    today.setMonth(0);
    return new Date(today.setFullYear(today.getFullYear() - 1));
  }

  static getLastYearEndDate(): Date {
    const today = new Date();
    today.setDate(1);
    today.setMonth(0);
    return new Date(today.setDate(today.getDate() - 1));
  }

  static isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }
}
